import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ShoppingCart, Trash2, Plus, Minus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ ppn_enabled: false, ppn_rate: 1.0 });

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const res = await axios.get(`${API}/cart/`);
        setCartItems(res.data);
      } catch (err) {
        toast.error('Gagal memuat keranjang');
      } finally {
        setLoading(false);
      }
    };
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/`);
        setSettings({ ppn_enabled: res.data.ppn_enabled ?? false, ppn_rate: res.data.ppn_rate ?? 1.0 });
      } catch {}
    };
    fetchCart();
    fetchSettings();
  }, []);

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.custom_package?.price || item.package?.price || 0;
      return total + (price * (item.quantity || 1));
    }, 0);
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`);
      setCartItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Item dihapus dari keranjang');
    } catch {
      toast.error('Gagal menghapus item');
    }
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast.error('Keranjang kosong');
      return;
    }
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen py-12 bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const formatPrice = (p) => `Rp ${(p || 0).toLocaleString('id-ID')}`;

  return (
    <div className="min-h-screen py-12 bg-background">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold font-['Outfit'] mb-8">Keranjang Belanja</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cartItems.length === 0 ? (
              <Card className="rounded-2xl">
                <CardContent className="py-12 text-center">
                  <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">Keranjang Kosong</h3>
                  <p className="text-muted-foreground mb-6">
                    Belum ada paket umrah di keranjang Anda
                  </p>
                  <Link to="/packages">
                    <Button className="rounded-full">Lihat Paket Umrah</Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              cartItems.map((item) => {
                const pkg = item.custom_package || item.package || {};
                const price = pkg.price || 0;
                return (
                  <Card key={item.id} className="rounded-2xl">
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">
                            {pkg.package_name || 'Paket Kustom Umrah Mandiri'}
                          </h3>
                          <div className="text-sm text-muted-foreground space-y-1">
                            {pkg.departure_date && <p>📅 Berangkat: {pkg.departure_date}</p>}
                            {pkg.return_date && <p>📅 Pulang: {pkg.return_date}</p>}
                            {pkg.departure_city && <p>📍 {pkg.departure_city}</p>}
                            {item.package?.hotel && <p>🏨 {item.package.hotel}</p>}
                            {item.package?.airline && <p>✈️ {item.package.airline}</p>}
                            {item.package?.duration && <p>📅 {item.package.duration} Hari</p>}
                          </div>
                          <p className="font-semibold text-lg mt-3">
                            {formatPrice(price)} / orang
                          </p>
                        </div>

                        <div className="flex flex-col items-end justify-between">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                          <p className="font-bold text-lg">
                            {formatPrice(price * (item.quantity || 1))}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          <div>
            <Card className="rounded-2xl sticky top-4">
              <CardHeader>
                <CardTitle>Ringkasan Belanja</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{formatPrice(calculateTotal())}</span>
                  </div>
                  {settings.ppn_enabled && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">PPN ({settings.ppn_rate}%)</span>
                      <span>{formatPrice(calculateTotal() * settings.ppn_rate / 100)}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>{formatPrice(settings.ppn_enabled ? calculateTotal() * (1 + settings.ppn_rate / 100) : calculateTotal())}</span>
                  </div>
                </div>

                <Button
                  className="w-full rounded-full"
                  size="lg"
                  onClick={handleCheckout}
                  disabled={cartItems.length === 0}
                >
                  Lanjut ke Pembayaran
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
